'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import HanziWriter from 'hanzi-writer'
import type { SearchResult } from '../../types/dictionary'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { MinorHeading, MutedText, LabelText } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { useChineseFont } from '@/lib/chinese-font'

type ScriptType = 'simplified' | 'traditional'

interface Props {
  entry: SearchResult
  primaryText?: string | null
  secondaryText?: string | null
  primaryScriptType: ScriptType
  secondaryScriptType: ScriptType
}

type HanziWriterInstance = ReturnType<typeof HanziWriter.create>

export function CharacterDrawingTab({
  entry,
  primaryText,
  secondaryText,
  primaryScriptType,
  secondaryScriptType
}: Props) {
  const chineseFont = useChineseFont()

  const fallbackFromEntry =
    primaryScriptType === 'traditional'
      ? entry.traditional || entry.simplified
      : entry.simplified || entry.traditional

  const resolvedText =
    (primaryText && primaryText.trim().length > 0 ? primaryText : '') ||
    (fallbackFromEntry && fallbackFromEntry.trim().length > 0
      ? fallbackFromEntry
      : '') ||
    (secondaryText && secondaryText.trim().length > 0 ? secondaryText : '') ||
    entry.simplified ||
    entry.traditional ||
    ''

  const characters = useMemo(
    () =>
      Array.from(resolvedText).filter(
        (char) => typeof char === 'string' && char.trim().length > 0
      ),
    [resolvedText]
  )

  const [selectedCharPreference, setSelectedCharPreference] = useState<
    string | null
  >(() => characters[0] || null)
  const [loopChar, setLoopChar] = useState<string | null>(null)
  const [quizChar, setQuizChar] = useState<string | null>(null)
  const [writerSeed, setWriterSeed] = useState(0)
  const [canvasSize, setCanvasSize] = useState(260)

  const canvasWrapperRef = useRef<HTMLDivElement | null>(null)
  const canvasRef = useRef<HTMLDivElement | null>(null)
  const writerRef = useRef<HanziWriterInstance | null>(null)

  const selectedChar =
    selectedCharPreference && characters.includes(selectedCharPreference)
      ? selectedCharPreference
      : characters[0] || ''

  useEffect(() => {
    const wrapper = canvasWrapperRef.current
    if (!wrapper || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const maxSize = Math.min(Math.max(entry.contentRect.width - 8, 160), 360)
      setCanvasSize(Math.round(maxSize))
    })

    observer.observe(wrapper)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const target = canvasRef.current
    if (!target || !selectedChar) return

    target.innerHTML = ''

    const writer = HanziWriter.create(target, selectedChar, {
      width: canvasSize,
      height: canvasSize,
      padding: 12,
      showOutline: true,
      showCharacter: true,
      strokeAnimationSpeed: 1,
      delayBetweenStrokes: 200,
      delayBetweenLoops: 1500,
      highlightColor: '#0ea5e9',
      radicalColor: '#0ea5e9'
    })

    writerRef.current = writer

    return () => {
      writer.cancelQuiz()
      writerRef.current = null
    }
  }, [selectedChar, canvasSize, writerSeed])

  useEffect(() => {
    if (!writerRef.current || !canvasSize) return
    writerRef.current.updateDimensions({
      width: canvasSize,
      height: canvasSize
    })
  }, [canvasSize])

  const isLooping = loopChar !== null && loopChar === selectedChar
  const isQuizzing = quizChar !== null && quizChar === selectedChar

  const handleAnimate = useCallback(() => {
    const writer = writerRef.current
    if (!writer) return
    writer.cancelQuiz()
    setLoopChar(null)
    setQuizChar(null)
    void writer.animateCharacter()
  }, [])

  const handleLoopToggle = useCallback(async () => {
    const writer = writerRef.current
    if (!writer) return

    if (isLooping) {
      await writer.pauseAnimation()
      setLoopChar(null)
      return
    }

    writer.cancelQuiz()
    setQuizChar(null)
    await writer.loopCharacterAnimation()
    setLoopChar(selectedChar)
  }, [isLooping, selectedChar])

  const handleQuizToggle = useCallback(async () => {
    const writer = writerRef.current
    if (!writer) return

    if (isQuizzing) {
      writer.cancelQuiz()
      setQuizChar(null)
      return
    }

    await writer.pauseAnimation()
    setLoopChar(null)
    await writer.quiz({
      showCharacter: false,
      showOutline: true,
      showHintAfterMisses: 2,
      highlightOnComplete: true
    })
    setQuizChar(selectedChar)
  }, [isQuizzing, selectedChar])

  const handleReset = useCallback(async () => {
    if (writerRef.current) {
      writerRef.current.cancelQuiz()
      await writerRef.current.pauseAnimation()
    }
    setLoopChar(null)
    setQuizChar(null)
    setWriterSeed((seed) => seed + 1)
  }, [])

  if (characters.length === 0) {
    return (
      <Card className="bg-muted/30">
        <CardContent className="py-12 text-center">
          <MutedText className="text-base">
            No characters available for drawing.
          </MutedText>
        </CardContent>
      </Card>
    )
  }

  const scriptLabel =
    resolvedText === primaryText
      ? primaryScriptType
      : resolvedText === secondaryText
        ? secondaryScriptType
        : primaryScriptType

  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <MinorHeading className="text-xs tracking-wider uppercase">
            Characters
          </MinorHeading>
          <LabelText className="text-muted-foreground text-xs capitalize">
            {scriptLabel} script
          </LabelText>
        </div>
        <div className="flex flex-wrap gap-2">
          {characters.map((char, idx) => (
            <Button
              key={`${char}-${idx}`}
              variant={char === selectedChar ? 'default' : 'outline'}
              size="sm"
              className={cn(
                'min-w-[48px] border-dashed',
                chineseFont,
                char === selectedChar
                  ? 'text-base'
                  : 'text-muted-foreground text-base'
              )}
              onClick={() => {
                setSelectedCharPreference(char)
                setLoopChar(null)
                setQuizChar(null)
                setWriterSeed((seed) => seed + 1)
              }}
            >
              {char}
            </Button>
          ))}
        </div>
      </div>

      <Card className="border-dashed">
        <CardContent className="space-y-5 p-5">
          <div
            ref={canvasWrapperRef}
            className="bg-muted/40 mx-auto w-full max-w-sm rounded-2xl border border-dashed p-4"
          >
            <div ref={canvasRef} className="aspect-square w-full" />
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            <Button
              onClick={handleAnimate}
              disabled={!selectedChar || isQuizzing}
            >
              Animate Once
            </Button>
            <Button
              variant={isLooping ? 'secondary' : 'outline'}
              onClick={() => void handleLoopToggle()}
              disabled={!selectedChar || isQuizzing}
            >
              {isLooping ? 'Stop Loop' : 'Loop Animation'}
            </Button>
            <Button
              variant={isQuizzing ? 'secondary' : 'outline'}
              onClick={() => void handleQuizToggle()}
              disabled={!selectedChar}
            >
              {isQuizzing ? 'Stop Quiz' : 'Start Quiz'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => void handleReset()}
              disabled={!selectedChar}
            >
              Reset
            </Button>
          </div>

          <MutedText className="text-sm leading-relaxed">
            Use <span className="font-semibold">Loop</span> to watch the stroke
            order repeatedly, or start the quiz to practice drawing the
            character yourself. Reset clears any ongoing animation or quiz
            state.
          </MutedText>
        </CardContent>
      </Card>
    </div>
  )
}
